import clsx from "clsx";
import { memo, useEffect, useRef, useState } from "react";
import noImage from "@/assets/img/no-image.svg";
import { isR18ThreadTagName, resolveUploadPath } from "@/entities/thread";
import { getArchiveLinkAttributes } from "@/features/archive/archiveCatalogBehavior";
import type { ArchiveThread } from "@/features/archive/types";
import { CatalogBoardIcon } from "@/features/catalog/components/CatalogBoardIcon";
import { CatalogThreadLink } from "@/features/catalog/components/navigation/CatalogThreadLink";
import { useCatalogStore } from "@/features/catalog/stores/catalogStore";
import { useSettingsStore } from "@/features/settings/hooks";
import { formatDateToMinute } from "@/shared/lib/date";

interface DesktopArchiveItemProps {
  thread: ArchiveThread;
  openInNewTab?: boolean;
}

/** 過去ログの1セル(PC版)。カタログのDesktopCatalogItemと同じ罫線グリッド流儀に揃える。 */
export const DesktopArchiveItem: React.FunctionComponent<DesktopArchiveItemProps> =
  memo(function DesktopArchiveItem({
    thread,
    openInNewTab,
  }: DesktopArchiveItemProps) {
    const title = thread.opExcerpt || "(無題)";
    const catalogOpenInNewTab = useCatalogStore((state) => state.openInNewTab);
    const textLines = useCatalogStore((state) => state.textLines);
    const showR18 = useSettingsStore((state) => state.showR18);
    const [r18Revealed, setR18Revealed] = useState(false);
    const linkRef = useRef<HTMLAnchorElement>(null);
    // ArchiveItem(モバイル版)と同様、空文字のサムネイルも本画像へフォールバックする
    const imageUrl = resolveUploadPath(thread.thumbnailUrl || "");
    const isR18 = thread.tags.some((tag) => isR18ThreadTagName(tag.name));
    const isR18Hidden =
      isR18 && Boolean(thread.thumbnailUrl) && !showR18 && !r18Revealed;
    const linkAttributes = getArchiveLinkAttributes(
      openInNewTab ?? catalogOpenInNewTab,
    );
    const archivedAtLabel = formatDateToMinute(thread.archivedAt);

    useEffect(() => {
      if (r18Revealed) linkRef.current?.focus();
    }, [r18Revealed]);

    return (
      <article className="desktop-catalog-item" aria-label={title}>
        {isR18Hidden && (
          <button
            type="button"
            className="desktop-catalog-r18-trigger"
            aria-label={`${title}のR18画像を表示`}
            onClick={(): void => setR18Revealed(true)}
          />
        )}
        <CatalogThreadLink
          ref={linkRef}
          thread={{ id: thread.threadId, b: thread.b }}
          {...linkAttributes}
        >
          <figure className="desktop-catalog-thumb">
            <img
              src={imageUrl}
              // 直下の.desktop-catalog-textに同じタイトルを表示するため、
              // 装飾画像として扱いスクリーンリーダーでの重複読み上げを避ける
              alt=""
              loading="lazy"
              decoding="async"
              className={clsx(isR18Hidden && "r18-spoiler-image")}
              onError={(
                event: React.SyntheticEvent<HTMLImageElement>,
              ): void => {
                event.currentTarget.src = noImage;
              }}
            />
          </figure>
          {textLines > 0 && (
            <p
              className="desktop-catalog-text catalog-text-clamp"
              style={
                {
                  "--catalog-text-lines": textLines,
                } as React.CSSProperties
              }
            >
              {title}
            </p>
          )}
          <p className="desktop-catalog-count desktop-archive-count">
            <data value={thread.replyCount}>{thread.replyCount}レス</data>
            <CatalogBoardIcon slug={thread.b} />
            <time className="archive-saved-at" dateTime={thread.archivedAt}>
              {archivedAtLabel}
            </time>
          </p>
        </CatalogThreadLink>
      </article>
    );
  });
